/**
 * Dashboard de Gestão Comercial
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  PeopleOutlined,
  CheckCircleOutlined,
  BusinessCenterOutlined,
  PhoneOutlined
} from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';

interface StatCardProps {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactElement;
  color: string;
}

interface DashboardStats {
  overview: {
    totalLeads: number;
    leadsThisMonth: number;
    leadsLastMonth: number;
    growthPercentage: number;
    leadsWithPhone: number;
    contactablePercentage: number;
    closedLeads: number;
    conversionRate: number;
    activeDeals: number;
  };
  leadsByStatus: Record<string, number>;
  groupsByStatus: Record<string, number>;
  topCities: Array<{ city: string; state: string; count: number }>;
  recentActivities: Array<{
    id: string;
    leadName: string;
    status: string;
    groupName: string;
    userName: string;
    createdAt: string;
  }>;
  agentPerformance: Array<{
    name: string;
    email: string;
    groups: number;
    leads: number;
    closed: number;
    active: number;
    conversionRate: string;
  }>;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon, color }) => {
  const isPositive = change >= 0;

  return (
    <Card
      sx={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255, 107, 53, 0.1)',
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'rgba(255, 107, 53, 0.3)',
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(255, 107, 53, 0.15)',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {title}
          </Typography>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              '& svg': {
                fontSize: 28,
              }
            }}
          >
            {icon}
          </Box>
        </Box>

        <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
          {value}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isPositive ? (
            <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />
          ) : (
            <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: isPositive ? '#4caf50' : '#f44336',
              fontWeight: 600,
            }}
          >
            {isPositive ? '+' : ''}{change}%
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            vs mês anterior
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Seletor de visualização (Admin)
  const [viewMode, setViewMode] = useState<'mine' | 'all' | 'individual'>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [availableUsers, setAvailableUsers] = useState<Array<{ uid: string; displayName: string; email: string }>>([]);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  useEffect(() => {
    loadDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, viewMode, selectedUserId]);

  const loadUsers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/users`
      );
      const users = await response.json();
      setAvailableUsers(users);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (user) {
        // Determinar qual userId e isAdmin usar baseado no viewMode
        if (isAdmin && viewMode === 'all') {
          // Visualização geral (todos os dados)
          queryParams.append('isAdmin', 'true');
        } else if (isAdmin && viewMode === 'individual' && selectedUserId) {
          // Visualização de um usuário específico
          queryParams.append('userId', selectedUserId);
          queryParams.append('isAdmin', 'false');
        } else {
          // Visualização própria (mine) ou usuário normal
          queryParams.append('userId', user.uid);
          queryParams.append('isAdmin', 'false');
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/dashboard/stats?${queryParams.toString()}`
      );
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewModeChange = (event: SelectChangeEvent) => {
    const newMode = event.target.value as 'mine' | 'all' | 'individual';
    setViewMode(newMode);
    if (newMode !== 'individual') {
      setSelectedUserId('');
    }
  };

  const handleUserChange = (event: SelectChangeEvent) => {
    setSelectedUserId(event.target.value);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 60) {
      return `${diffInMinutes} min atrás`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hora${diffInHours > 1 ? 's' : ''} atrás`;
    } else {
      return `${diffInDays} dia${diffInDays > 1 ? 's' : ''} atrás`;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <CircularProgress sx={{ color: '#FF6B35' }} />
        </Box>
      </MainLayout>
    );
  }

  if (!stats || !stats.overview || !stats.leadsByStatus || !stats.groupsByStatus) {
    return (
      <MainLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
            Erro ao carregar estatísticas
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Tente recarregar a página ou importe alguns leads
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  const { overview, leadsByStatus, groupsByStatus, topCities, recentActivities, agentPerformance } = stats;

  // Calcular valores para o pipeline
  const novoLeads = leadsByStatus['novo'] || 0;
  const contactadoLeads = leadsByStatus['contactado'] || 0;
  const emContatoLeads = (leadsByStatus['emContato'] || 0) + (leadsByStatus['em_contato'] || 0);
  const fechadoLeads = leadsByStatus['fechado'] || 0;

  const totalForPipeline = overview.totalLeads || 1;

  // Obter nome do usuário selecionado
  const getViewModeLabel = () => {
    if (viewMode === 'all') return 'Geral (Todos os Dados)';
    if (viewMode === 'mine') return 'Meus Dados';
    if (viewMode === 'individual' && selectedUserId) {
      const selectedUser = availableUsers.find(u => u.uid === selectedUserId);
      return `Dados de ${selectedUser?.displayName || selectedUser?.email || 'Usuário'}`;
    }
    return 'Individual';
  };

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              Dashboard Comercial
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {isAdmin ? getViewModeLabel() : 'Visão geral do desempenho comercial (seus dados)'}
            </Typography>
          </Box>

          {/* Seletor de Visualização (Admin) */}
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 2, minWidth: 300 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Visualização</InputLabel>
                <Select
                  value={viewMode}
                  label="Visualização"
                  onChange={handleViewModeChange}
                  sx={{
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                  }}
                >
                  <MenuItem value="all">📊 Geral (Todos)</MenuItem>
                  <MenuItem value="mine">👤 Meus Dados</MenuItem>
                  <MenuItem value="individual">👥 Usuário Específico</MenuItem>
                </Select>
              </FormControl>

              {viewMode === 'individual' && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Usuário</InputLabel>
                  <Select
                    value={selectedUserId}
                    label="Usuário"
                    onChange={handleUserChange}
                    sx={{
                      color: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6B35' },
                    }}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.uid} value={user.uid}>
                        {user.displayName || user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </Box>

        {/* Stats Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard
            title="Total de Leads"
            value={overview.totalLeads.toLocaleString('pt-BR')}
            change={overview.growthPercentage}
            icon={<PeopleOutlined />}
            color="#FF6B35"
          />
          <StatCard
            title="Leads com Telefone"
            value={`${overview.contactablePercentage}%`}
            change={overview.growthPercentage / 2}
            icon={<PhoneOutlined />}
            color="#4caf50"
          />
          <StatCard
            title="Negócios Ativos"
            value={overview.activeDeals}
            change={overview.growthPercentage * 0.8}
            icon={<BusinessCenterOutlined />}
            color="#2196f3"
          />
          <StatCard
            title="Taxa de Conversão"
            value={`${overview.conversionRate}%`}
            change={overview.conversionRate > 20 ? 5.7 : -2.3}
            icon={<CheckCircleOutlined />}
            color="#9c27b0"
          />
        </Box>

        {/* Main Content Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 3 }}>
          {/* Pipeline de Vendas */}
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
              Pipeline de Vendas
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Novos
                </Typography>
                <Typography variant="body2" sx={{ color: '#FF6B35', fontWeight: 600 }}>
                  {novoLeads} leads
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(novoLeads / totalForPipeline) * 100}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF6B35',
                    borderRadius: 1,
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Contactados
                </Typography>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  {contactadoLeads} leads
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(contactadoLeads / totalForPipeline) * 100}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4caf50',
                    borderRadius: 1,
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Em Contato
                </Typography>
                <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 600 }}>
                  {emContatoLeads} leads
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(emContatoLeads / totalForPipeline) * 100}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196f3',
                    borderRadius: 1,
                  }
                }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Fechados
                </Typography>
                <Typography variant="body2" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                  {fechadoLeads} leads
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(fechadoLeads / totalForPipeline) * 100}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#9c27b0',
                    borderRadius: 1,
                  }
                }}
              />
            </Box>
          </Paper>

          {/* Atividades Recentes */}
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
              Atividades Recentes
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentActivities.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', py: 4 }}>
                  Nenhuma atividade recente
                </Typography>
              ) : (
                recentActivities.slice(0, 5).map((activity, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 0.5 }}>
                      Lead: {activity.leadName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 0.5 }}>
                      Grupo: {activity.groupName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {formatTimeAgo(activity.createdAt)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Box>

        {/* Top Cidades */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
              Top Cidades
            </Typography>

            {topCities.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', py: 4 }}>
                Nenhum dado disponível
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {topCities.map((city, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {city.city} - {city.state}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${city.count} leads`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 107, 53, 0.2)',
                        color: '#FF6B35',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Performance por Agente (Admin) */}
          {isAdmin && agentPerformance.length > 0 && (
            <Paper
              sx={{
                p: 3,
                backgroundColor: '#1a1a1a',
                border: '1px solid rgba(255, 107, 53, 0.1)',
              }}
            >
              <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
                Performance dos Agentes
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', border: 'none' }}>Agente</TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', border: 'none' }}>Leads</TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', border: 'none' }}>Fechados</TableCell>
                      <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.7)', border: 'none' }}>Taxa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agentPerformance.map((agent, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: '#FFFFFF', border: 'none' }}>
                          {agent.name}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#FF6B35', border: 'none', fontWeight: 600 }}>
                          {agent.leads}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#4caf50', border: 'none', fontWeight: 600 }}>
                          {agent.closed}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#9c27b0', border: 'none', fontWeight: 600 }}>
                          {agent.conversionRate}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>

        {/* Status dos Grupos */}
        <Paper
          sx={{
            p: 3,
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255, 107, 53, 0.1)',
          }}
        >
          <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 3 }}>
            Status dos Grupos
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#FF6B35', fontWeight: 700, mb: 1 }}>
                {groupsByStatus['novo'] || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Grupos Novos
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 700, mb: 1 }}>
                {groupsByStatus['trabalhando'] || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Grupos Trabalhando
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                {groupsByStatus['finalizado'] || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Grupos Finalizados
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default Dashboard;
